import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, Typography, Grid, Divider, Box, CircularProgress, Button, TextField, MenuItem, Dialog, DialogContent, DialogTitle, IconButton, Alert } from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logo from "../assets/bdot-removebg-preview.png";
import { toWords } from "number-to-words";
import { numberToWordsINR } from "number-to-words-inr";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from "react-router-dom";
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Payslip = () => {
  const [employeeId, setEmployeeId] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [payslip, setPayslip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const payslipRef = useRef(null);
  const location = useLocation();

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/user/current`, {
          withCredentials: true
        });
        
        if (response.data && response.data.employee_id) {
          setEmployeeId(response.data.employee_id);
        }
      } catch (err) {
        //console.error("Error fetching current user:", err);
      }
    };
    
    fetchCurrentUser();
    setOpen(true);
  }, [location.pathname]);

  const fetchPayslip = async () => {
    if (!month || !year) {
      setError("Month and year are required.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
       const token = localStorage.getItem('token'); 
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/payslip/${month}/${year}`,  {
        headers: {
          Authorization: `Bearer ${token}`,
         
        },
        withCredentials:true
      });
      setPayslip(response.data.data);
      setOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch payslip");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert image URL to base64
  const getBase64Image = (imgUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = reject;
      img.src = imgUrl;
    });
  };

  const downloadPDF = async () => {
    if (!payslipRef.current) return;
    
    try {
      // Convert company logo to base64 if it exists
      if (payslip.company_details?.companyLogo) {
        const logoImg = payslipRef.current.querySelector('img[alt="bridgeme"]');
        if (logoImg) {
          const base64Logo = await getBase64Image(payslip.company_details.companyLogo);
          logoImg.src = base64Logo;
        }
      }
      
      // Small delay to ensure image is loaded
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(payslipRef.current, { 
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save(`Payslip_${payslip?.personal?.firstName}_${payslip?.month}_${payslip?.year}.pdf`);
    } catch (error) {
      //console.error('Error generating PDF:', error);
      // Fallback to original method
      const canvas = await html2canvas(payslipRef.current, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save(`Payslip_${payslip?.personal?.firstName}_${payslip?.month}_${payslip?.year}.pdf`);
    }
  };
 
  const printPayslip = () => {
    if (!payslipRef.current) return;
    const printContent = payslipRef.current.innerHTML;
    const printWindow = window.open("", "", "width=1000,height=11000");
    
    const styles = Array.from(document.styleSheets)
      .map((sheet) => {
        try {
          return Array.from(sheet.cssRules)
            .map((rule) => rule.cssText)
            .join("\n");
        } catch (error) {
          return ""; 
        }
      })
      .join("\n");
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Payslip</title>
          <style>${styles}</style>
        </head>
        <body>
          <div id="print-container">
            ${printContent}
          </div>
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `);
  
    printWindow.document.close();
    printWindow.print();
  };

  // Helper function to safely format numbers with toLocaleString
const formatCurrency = (value) => {
  return value != null ? Math.round(value).toLocaleString('en-IN') : '0';
};

  // Helper function to capitalize first letter of each word
  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  // Helper function to check if a value should be displayed (not 0 or null)
  const shouldDisplayValue = (value) => {
    return value != null && value > 0;
  };
  const hasAdditionalPayments =
    shouldDisplayValue(payslip?.reimbursements) ||
    shouldDisplayValue(payslip?.arrear_included);

  return (
    <>
      {!open && (
        <Box display="flex" justifyContent="left" mt={3}>
          <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
            Fetch Payslip
          </Button>
        </Box>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{           
    sx: {             
      height: '55vh',
      width: '800px',
      maxWidth: '90vw',
      margin: 'auto',
      marginLeft: '150px', // Adjust this value based on your sidebar width
    }         
  }}
  sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    '& .MuiDialog-container': {
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: '150px', // Same as sidebar width to offset centering
    }
  }}>
        <DialogTitle>
          Enter Payslip Details
          <IconButton onClick={() => setOpen(false)} sx={{ position: "absolute", right: 10, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Employee ID"
            variant="outlined"
            value={employeeId}
            disabled
            InputProps={{
              readOnly: true,
            }}
            margin="normal"
          />
          <TextField
            select
            fullWidth
            label="Select Month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            margin="normal"
          >
            {months.map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Select Year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            margin="normal"
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </TextField>

          {error && <Typography color="error" align="center" mt={2}>{error}</Typography>}

          <Box display="flex" justifyContent="center" mt={3} gap={2}>
            <Button variant="contained" color="primary" onClick={fetchPayslip}>
              Get Payslip
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {loading && <CircularProgress sx={{ display: "block", mx: "auto", mt: 2 }} />}

      {payslip && (
        <Card ref={payslipRef} sx={{ maxWidth: "95%", p: 3, mt: 4, boxShadow: 3, mx: 5 }}>
          <CardContent>
            <Box>
              <Typography textAlign="Right">Computer Generated</Typography>
            </Box>
            {/* <Typography variant="h5" fontWeight="bold" textAlign="center" marginBottom="10px"><strong>Employee Payslip</strong></Typography> */}
            {/* <Typography
  variant="h6"
  fontWeight="bold"
  textAlign="center"
>
  {payslip.company_details?.companyNames || "Bridgeme Technologies Private Limited"}
</Typography>
<Typography
  variant="body2"
  textAlign="center"
  color="textSecondary"
  marginRight="20px"
>
  {payslip.company_details?.addresss ||
    "#504, BUDA Scheme 6, Rani Channamma Nagar, 2nd Stage, Belgaum, Karnataka, India - 590006"}
</Typography>
 <Box marginLeft={10}>
                {payslip.company_details?.companyLogo && (
                  <img 
                    src={payslip.company_details.companyLogo} 
                    alt="companylogo" 
                    width={80}
                    crossOrigin="anonymous"
                    style={{ maxWidth: '70px', height: 'auto' }}
                  />
                )}
              </Box> */}
<Box position="relative" mt={2} mb={2}>

  {/* Logo – fixed left */}
{payslip.company_details?.companyLogo && (
  <Box
    position="absolute"
    left={4}
    top={2}
    sx={{
      width: 290,
      height: 120, // Adjust based on your header height
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <img
      src={payslip.company_details.companyLogo}
      alt="companylogo"
      crossOrigin="anonymous"
      style={{
        maxWidth: "100%",
        maxHeight: "100%",
        width: "auto",
        height: "auto",
        objectFit: "contain",
      }}
    />
  </Box>
)}

  {/* Centered Company Info */}
  <Box textAlign="center" >
    <Typography variant="h6" fontWeight="bold" paddingTop={2}>
      {payslip.company_details?.companyName ||
        "Company Name"}
    </Typography>

   <Typography
  variant="body2"
  color="textSecondary"
  textAlign="center"
  sx={{
    maxWidth: "40%",
    margin: "0 auto",
    wordBreak: "break-word",
    whiteSpace: "normal"
  }}
>
  {payslip.company_details?.address ||
    "Company Address"}
</Typography>
  </Box>

</Box>
            <Box display="flex" justifyContent="space-between" flexDirection="row" mb={2}>
              {/* <Box marginLeft={10}>
                {payslip.company_details?.companyLogo && (
                  <img 
                    src={payslip.company_details.companyLogo} 
                    alt="companylogo" 
                    width={80}
                    crossOrigin="anonymous"
                    style={{ maxWidth: '70px', height: 'auto' }}
                  />
                )}
              </Box> */}
              {/* <Box>
                              <Box marginTop={2} display="flex" flexDirection="column" alignItems="center" textAlign="center" marginRight={40} marginLeft={25} >
                <Typography variant="body1">
                  {payslip.company_details?.addresss || "Bridgeme Technologies Private Limited"}
                </Typography>
                <Typography variant="body1">
                  {payslip.company_details?.addresss || "#504, BUDA SCHEME 6, Rani Channamma Nagar, 2nd Stage, Belgaum, Karnataka, India 590006"}
                </Typography>
                </Box>
              </Box> */}
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight="bold">
              </Typography>
       <Typography variant="body2" color="textSecondary">
  {payslip.month} {payslip.year} | Created On:{" "}
  {payslip.createdAt
    ? new Date(payslip.createdAt).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "N/A"}
</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold" textAlign="center">Employee Payslip for {payslip.month}-{payslip.year}</Typography>
              
            </Box>
            <Divider sx={{ my: 2 }} />

            {payslip.arrear_details?.has_arrear && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> This payslip includes an arrear of ₹{formatCurrency(payslip.arrear_details.arrear_amount)} from {payslip.arrear_details.arrear_source}
                </Typography>
              </Alert>
            )}
            {/* ✅ PAYMENT STATUS ALERT */}
            {/* {payslip.payment_status?.status === "arrear_generated" && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Payment Status:</strong> Partial payment received. Expected: ₹{formatCurrency(payslip.payment_status.expected_salary)}, Paid: ₹{formatCurrency(payslip.payment_status.actual_paid)}. Shortfall: ₹{formatCurrency(Math.abs(payslip.payment_status.difference))}
                </Typography>
              </Alert>
            )}
            {payslip.payment_status?.status === "paid" && payslip.payment_status.difference === 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Payment Status:</strong> Full payment received on {new Date(payslip.payment_status.paid_on).toLocaleDateString()}
                </Typography>
              </Alert>
            )} */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box p={2} sx={{ backgroundColor: "#f0f0f0", borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight="bold">Employee Details</Typography>
                  <Typography><strong>Employee_ID:</strong> {payslip.employee_id || "N/A"}</Typography>
                  <Typography><strong>Name:</strong> {payslip.personal?.firstName || "N/A"} {payslip.personal?.lastName || "N/A"}</Typography>
                  <Typography><strong>Email:</strong> {payslip.personal?.companyemail || "N/A"}</Typography>
                  <Typography><strong>Designation:</strong> {payslip.financial_details?.department || "N/A"}</Typography>
                  <Typography><strong>Phone Number:</strong> {payslip.personal?.phoneNumber || "N/A"}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box p={2} sx={{ backgroundColor: "#f0f0f0", borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight="bold">Financial Details</Typography>
                  <Typography><strong>Bank Name:</strong> {payslip.financial_details?.bankName || "N/A"}</Typography>
                  <Typography><strong>IFSC NO:</strong> {payslip.financial_details?.ifscCode || "N/A"}</Typography>
                  <Typography><strong>A/C No:</strong> {payslip.financial_details?.accountNumber || "N/A"}</Typography>
                  <Typography><strong>PF No:</strong> {payslip.pfDetails?.pfno || "N/A"}</Typography>
                  <Typography><strong>UAN:</strong> {payslip.pfDetails?.UAN || "N/A"}</Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography variant="h6" fontWeight="bold">Salary Details</Typography>
                <Grid container>
                  <Grid item xs={6}>
                    <Typography fontWeight="bold">Component</Typography>
                    <Typography>Basic Salary</Typography>
                    <Typography>HRA</Typography>
                    <Typography>Special Allowance</Typography>
                   

                    {shouldDisplayValue(payslip.variable_salary) && (
                      <Typography>Variable Salary</Typography>
                    )}
                    {shouldDisplayValue(payslip.joining_bonus) && (
                      <Typography>Joining Bonus</Typography>
                    )}
                   
                    {shouldDisplayValue(payslip.ot_amount) && (
    <Typography>OT Amount ({payslip.ot_hours || 0} hrs)</Typography>

)}
{shouldDisplayValue(payslip.lop_deduction) && (

    <Typography color="red">LOP ({payslip.lop_days || 0} days)</Typography>

)}
                                        <Typography sx={{color:"white"}} >-</Typography>                 
                                        <Typography sx={{color:"white"}} >-</Typography>                 

                    <Divider sx={{ my: 1 }} />
                    <Typography fontWeight="bold">Gross Salary</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography fontWeight="bold">Amount</Typography>
                    <Typography>₹{formatCurrency(payslip.base_salary)}</Typography>
                    <Typography>₹{formatCurrency(payslip.hra)}</Typography>
                    <Typography>₹{formatCurrency(payslip.special_allowance)}</Typography>
                    

            
                    {shouldDisplayValue(payslip.variable_salary) && (
                      <Typography>₹{formatCurrency(payslip.variable_salary)}</Typography>
                    )}
                    {shouldDisplayValue(payslip.joining_bonus) && (
                      <Typography>₹{formatCurrency(payslip.joining_bonus)}</Typography>
                    )}
                   
                    {shouldDisplayValue(payslip.ot_amount) && (
                      <Typography>₹{formatCurrency(payslip.ot_amount)}</Typography>
                    )}
                     {shouldDisplayValue(payslip.lop_deduction) && (
                      <Typography color="red">-₹{formatCurrency(payslip.lop_deduction)}</Typography>
                    )}
                                                            <Typography sx={{color:"white"}} >-</Typography>                 
                                                            <Typography sx={{color:"white"}} >-</Typography>                 

                    <Divider sx={{ my: 1 }} />
                    <Typography fontWeight="bold">₹{formatCurrency(payslip.gross_salary)}</Typography>
                  </Grid>
               <Grid item xs={3}>
                    <Typography fontWeight="bold">Cumulative</Typography>
                    <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.base_salary?.cumulative)}</Typography>
                    <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.hra?.cumulative)}</Typography>
                    <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.special_allowance?.cumulative)}</Typography>
                   
                    {shouldDisplayValue(payslip.variable_salary) && (
                      <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.variable_salary?.cumulative)}</Typography>
                    )}
                    {shouldDisplayValue(payslip.joining_bonus) && (
                      <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.joining_bonus?.cumulative)}</Typography>
                    )}
                    {shouldDisplayValue(payslip.ot_amount) && (
                      <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.ot_amount?.cumulative)}</Typography>
                    )}
                    {shouldDisplayValue(payslip.lop_deduction) && (
                      <Typography color="red">-₹{formatCurrency(payslip.cumulative_salary_details?.lop_deduction?.cumulative)}</Typography>
                    )}
                                          <Typography sx={{color:"white"}} >-</Typography>                 
                                          <Typography sx={{color:"white"}} >-</Typography>                 

                    <Divider sx={{ my: 1 }} />
                    <Typography fontWeight="bold">₹{formatCurrency(payslip.cumulative_salary_details?.gross_salary?.cumulative)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" fontWeight="bold">Deduction Details</Typography>
                <Grid container>
                  <Grid item xs={6}>
                    <Typography fontWeight="bold">Component</Typography>
                    <Typography>PF(Ee+Er)</Typography>
                       {shouldDisplayValue(payslip.vpf) && (
    <Typography>VPF</Typography>
                    )}
                    <Typography>Professional Tax</Typography>
                    <Typography>Income Tax</Typography>
                    <Typography>Gratuity </Typography>
                      <Typography>Medical Insurance</Typography>
                  {shouldDisplayValue(payslip.advance_recovery) && (
    <Typography>Advance Recovery</Typography>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography fontWeight="bold">Total Deductions</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography fontWeight="bold">Amount</Typography>
                    <Typography>₹{formatCurrency((payslip.employeepf)+(payslip.employerpf))}</Typography>
                          {shouldDisplayValue(payslip.vpf) && (
          <Typography>₹{formatCurrency(payslip.vpf)}</Typography>
        )}
                    <Typography>₹{formatCurrency(payslip.professional_tax)}</Typography>
                    <Typography>₹{formatCurrency(payslip.monthly_tax)}</Typography>
                    <Typography>₹{formatCurrency(payslip.gratuity)}</Typography>
                    
                      <Typography>₹{formatCurrency(payslip.medicalInsurance)}</Typography>

          {shouldDisplayValue(payslip.advance_recovery) && (
          <Typography>₹{formatCurrency(payslip.advance_recovery)}</Typography>
        )}
                    <Divider sx={{ my: 1 }} />
                    <Typography fontWeight="bold">₹{formatCurrency(payslip.total_deductions)}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography fontWeight="bold">Cumulative</Typography>
                    <Typography>₹{formatCurrency((payslip.cumulative_salary_details?.employeepf?.cumulative || 0) + (payslip.cumulative_salary_details?.employerpf?.cumulative || 0))}</Typography>
          {shouldDisplayValue(payslip.vpf) && (
          <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.vpf?.cumulative)}</Typography>
        )}                    <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.professional_tax?.cumulative)}</Typography>
                    <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.monthly_tax?.cumulative)}</Typography>
                    <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.gratuity?.cumulative)}</Typography>
                      <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.medicalInsurance?.cumulative)}</Typography>

          {shouldDisplayValue(payslip.advance_recovery) && (
          <Typography>₹{formatCurrency(payslip.cumulative_salary_details?.advance_recovery?.cumulative)}</Typography>
        )}
                    <Divider sx={{ my: 1 }} />
                    <Typography fontWeight="bold">₹{formatCurrency(payslip.cumulative_salary_details?.total_deductions?.cumulative)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

{hasAdditionalPayments && (
  <Box>
    <Typography variant="h6" fontWeight="bold">
      Additionals
    </Typography>

    {shouldDisplayValue(payslip.reimbursements) && (
      <Typography>
        Reimbursement : ₹{formatCurrency(payslip.reimbursements)}
      </Typography>
    )}

    {shouldDisplayValue(payslip.arrear_included) && (
      <Typography>
        Arrear({payslip.arrear_details.arrear_source}) : ₹{formatCurrency(payslip.arrear_included)}
      </Typography>
    )}

    <Divider sx={{ my: 2 }} />
  </Box>
)}

         <Typography variant="h5" fontWeight="bold">
  Net Salary: ₹{formatCurrency(payslip.net_salary)}
</Typography>   
            <Divider sx={{ my: 1 }} />

            <Typography variant="h6" fontWeight="bold" color="black">
              <strong>Rupees in words: </strong>
              {payslip.net_salary != null ? `${capitalizeWords(numberToWordsINR(payslip.net_salary))} Only` : 'Zero Only'}
              {/* {payslip.net_salary != null ? `${capitalizeWords(toWords(payslip.net_salary))} Only` : 'Zero Only'} */}
            </Typography>

            {/* Additional Information */}
            {payslip.medicalInsurance>0 && (
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  * Medical insurance deduction applied for this month
                </Typography>
              </Box>
            )}
             {payslip.arrear_details?.has_arrear && (
                <Typography variant="body2" color="orange" fontWeight="bold">
                  * Arrear amount of ₹{formatCurrency(payslip.arrear_details.arrear_amount)} from {payslip.arrear_details.arrear_source} is included in total payable
                </Typography>
              )}
          </CardContent>
        </Card>
      )}
      {payslip && (
        <Box display="flex" justifyContent="center" mt={2} gap={2}>
          <Button variant="outlined" sx={{ backgroundColor: "primary" }} color="primary" onClick={printPayslip}>
            Print
          </Button>
          <Button variant="outlined" color="primary" sx={{ backgroundColor: "primary" }} onClick={downloadPDF}>
            Download PDF
          </Button>
        </Box>
      )}
    </>
  );
};

export default Payslip;